import { createContext, useContext } from "react";

/**
 * Chat Action Context Interface
 *
 * Defines all the event handlers that can be triggered from within chat messages.
 * This context eliminates prop drilling through the ChatMessage component.
 */
export interface ChatActionContextType {
  onViewQuote?: (messageId: string) => void;
  onQuoteAction?: (messageId: string) => void;
  onContractAction?: (messageId: string) => void;
  onContractReject?: (messageId: string) => void;
  onViewContract?: (contractId: string) => void;
  onPrintReceipt?: (messageId: string) => void;
  onDownloadFile?: (url: string, fileName: string) => void;
  onRequestContractDocuments?: (
    messageId: string,
    paperCodes: number[],
    idempotencyKey: string,
  ) => Promise<void>;
  /**
   * 공급사가 [계약 서류 관리] 카드의 직접 제출 서류에 파일 첨부.
   * 성공 시 카드가 실시간 갱신되어야 하므로 refetch/invalidate 는 콜백 구현체에서 처리.
   */
  onUploadContractDocumentFile?: (
    roomId: string,
    paperCode: number,
    file: File,
  ) => Promise<void>;
  /**
   * 공급사가 [계약 서류 관리] 카드의 auto_pending_input 서류(135/164/188/195/222)에
   * documentInputs 값을 제출. 백엔드가 c-market 을 호출해 서류를 발급한다.
   * paperCode 135 + "YES" 포함 → 이 단계에선 answers 만 저장되고 buyer 확인이 별도로 필요하다.
   */
  onSubmitContractDocumentInput?: (
    roomId: string,
    paperCode: number,
    body: {
      contractRestrictionConfirm?: { answers: string[] };
      // c-market spec: 해당없음 = { rows: [] }. null 로 보내면 400.
      retireeRoster?: {
        rows: Array<{
          name: string;
          position: string;
          joinDate?: string | null;
          retireType?: string | null;
          servicePeriod?: string | null;
          etc?: string | null;
        }>;
      };
    },
  ) => Promise<void>;
  /**
   * 발주기관이 paperCode 135 최종 확인 응답을 제출. 저장된 answers 와 buyerConfirmation 이
   * 백엔드에서 묶여 c-market POST 로 나가고 서류가 발급된다. 1회 확정(재제출 불가).
   */
  onSubmitContractDocumentBuyerConfirmation?: (
    roomId: string,
    buyerConfirmation: "YES" | "NO",
  ) => Promise<void>;
  /**
   * 계약 카드의 [계산서 발급요청]/[카드결제하기] 클릭. buyer 앱이 임베드 안에서만 주입한다.
   *
   * 세모는 여기서 아무 것도 처리하지 않는다 — 대금은 발주기관이 공급기업에게 직접 지급하고,
   * 실제 요청·결제는 씨마켓 '나의 계약 관리' 에서 일어난다. 이 핸들러는 부모(씨마켓)에게
   * 그 화면으로 데려가 달라고 알리는 일만 한다.
   */
  onOpenCmarketContracts?: (
    contractId: string,
    channel: "cash" | "card",
  ) => void;
  onRefresh?: () => void;
  onRetry?: (messageId: string) => void;
  onImageLoad?: () => void;
}

export const ChatActionContext = createContext<
  ChatActionContextType | undefined
>(undefined);

export const useChatActions = () => {
  const context = useContext(ChatActionContext);
  if (!context) {
    throw new Error("useChatActions must be used within a ChatActionProvider");
  }
  return context;
};
